<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-executive-reports-page" element-loading-text="Loading...">
    <div class="button-flex">
      <div class="page-title">Bot Management Report</div>
      <div class="page-body">
        <div class="page-body-header">
          <div class="page-body-title">{{selectedItemLabel}}</div>
          <div v-if="iframeItems && iframeItems.length > 1" class="page-body-options">
            <el-select v-model="selectedFileName" class="date-selections" popper-class="wy-executive-reports-page-dropdown" size="small" @change="selectChangedHandler">
              <el-option
                v-for="item in iframeItems"
                :key="item.fileName"
                :label="item.label"
                :disabled="(item.fileName=='-' || selectedItem.fileName==item.fileName)"
                :value="item.fileName">
              </el-option>
            </el-select>
          </div>
        </div>
        <div class="page-body-content" v-loading="loading">
          <div v-show="iframeErrorMessage" class="page-body-error" v-html="iframeErrorMessage" />
          <iframe v-show="!iframeErrorMessage" ref="iframe" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';

export default Vue.extend({
  name: 'wy-executive-reports-page',
  data() {
    return {
      loading: false,
      selectedFileName: null,
      selectedItem: null,
      iframeItems: null,
      iframeUrl: null,
      iframeErrorMessage: null,

      noFileContentMsg: 'No report uploaded. To make Bot Management Report available, please provide a valid PDF file to the Wysdom DevOps team. Once uploaded, you can use the Configuration JSON to map the file. For assistance, please reach out to <a href="mailto:opscentr@wysdom.ai">opscentr@wysdom.ai</a>.',
      errorFileContentMsg: 'There was an issue with the PDF file uploaded. The file might be missing, corrupted, or not in the correct format. For support receiving business insights, please contact your System Administrator or reach out to <a href="mailto:oc_support@wysdom.ai">oc_support@wysdom.ai</a>.'
    }
  },

  computed: {
    ...mapGetters(['companyConfig']),

    selectedItemLabel() {
      if(this.selectedItem && this.selectedItem.fileName ==  "-") {
        return this.iframeItems.length>1 ? this.iframeItems[1].label : "";
      }
      else {
        return this.selectedItem ? this.selectedItem.label : "";
      }
    }
  },

  methods: {
    ...mapActions(['fetchFileContentUrl']),

    async getFileContentUrl(fileName) {
      return await this.fetchFileContentUrl({fileName: fileName});
    },
    
    async selectChangedHandler(key) {
      this.loading = true;
      this.selectedFileName = key;
      this.selectedItem = this.iframeItems.find(el => el.fileName == key);
      const fileContentUrl = await this.getFileContentUrl(this.selectedItem.fileName);
      if(fileContentUrl) {
        this.iframeErrorMessage = null;
        this.$refs.iframe.src = fileContentUrl;
      }
      else {
        this.iframeErrorMessage = this.errorFileContentMsg;
        this.loading = false;
      }

      this.$router.logAmplitudeEvent({ eventName: `BI_BMR_Bot_Management_Report: View Past Reports`, eventInfo: {"selected_report_name": this.selectedItem.label} });
    }
  },

  async mounted() {
    this.$refs.iframe.addEventListener( "load", () => {
      this.loading = false;
    });

    this.iframeItems = this.companyConfig.businessInsightBmrFiles ? JSON.parse(JSON.stringify(this.companyConfig.businessInsightBmrFiles)) : [];
    this.iframeItems.unshift({label: "View past reports", fileName: "-"});
    this.selectedItem = this.iframeItems[0];
    this.selectedFileName = this.selectedItem.fileName;

    if(this.iframeItems.length > 1) {
      const fileContentUrl = await this.getFileContentUrl(this.iframeItems[1].fileName);
      if(fileContentUrl) {
        this.iframeErrorMessage = null;
        if(this.$refs.iframe) this.$refs.iframe.src = fileContentUrl;
      }
      else {
        this.iframeErrorMessage = this.errorFileContentMsg;
      }
    }
    else {
      this.iframeErrorMessage = this.noFileContentMsg;
    }
  }
});
</script>

<style lang="scss">
.wy-executive-reports-page {
  min-height: calc(100% - 30px);

  .button-flex {
    height: 26px;
    padding-bottom: 12px;
  }

  .page-title{
    margin-bottom: 0.8vw;
    font-weight: 600;
    font-size: 25px;
    color: var(--site-font-secondary-color);
    line-height: 18px;
  }

  .page-body {
    box-sizing: border-box;
    margin-bottom: 10px;
    min-height: 300px;
    width: 100%;
    background-color: #fff;
    border: 1px solid #cad2da;
    border-radius: 5px;
    margin-top: 45px;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;

    .page-body-header {
      display: flex;
      border-bottom: 1px solid #CAD2DA;
    }

    .page-body-title {
      flex: 1;
      padding: 20px;
      font-style: normal;
      font-weight: 500;
      font-size: 18px;
      line-height: 22px;
      color: var(--site-font-secondary-color);      
    }

    .page-body-options {
      flex: 1;
      padding-top: 16px;
      padding-right: 16px;
      text-align: right;      

      .date-selections {
        input.el-input__inner {
          border-width: 1px !important;
          border-radius: 3px;
        }

        .el-input.is-focus .el-input__inner {
          border: 1px solid #DCDFE6 !important;
        }

        .el-input .el-input__inner:hover {
          border: 1px solid #DCDFE6 !important;
        }
        
        .el-input .el-input__inner:focus {
          border-color: #106FBC !important;
        }

        input {
          width: 140px;
        }
      }

      input::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
        color: var(--site-font-secondary-color);
        opacity: 1; /* Firefox */
      }

      input:-ms-input-placeholder { /* Internet Explorer 10-11 */
        color: var(--site-font-secondary-color);
      }

      input::-ms-input-placeholder { /* Microsoft Edge */
        color: var(--site-font-secondary-color);
      }
    }

    .page-body-content {
      .page-body-error {
        height: calc(100vh - 320px);
        color: #666;
        padding: 30px; 
        font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      }
      iframe {
        border: 0px solid #eee;
        width: 100%;
        height: calc(100vh - 260px);
        display:block; 
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;
      }
    }
  }
}

.wy-executive-reports-page-dropdown {
  .el-select-dropdown__item.hover, .el-select-dropdown__item:hover {
    background-color: #E8F1FF !important;
  }
}
</style>